import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Modal,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import { formatCustomDate } from "../../../utils/formatters";
import { modalStyle } from "../../../utils/styles";

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tab-panel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const ServiceCenterDetailsModal = ({ open, onClose, serviceCenter }) => {
  const [tabIndex, setTabIndex] = useState(0); // State for tracking active tab

  if (!serviceCenter) return null; // Handle case where serviceCenter is null

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue); // Update the tab index on change
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" component="h2">
          {serviceCenter.title}
        </Typography>
        <Typography variant="body1">Phone: {serviceCenter.phone}</Typography>
        <Typography variant="body1">Email: {serviceCenter.email}</Typography>
        <Typography variant="body1">
          Contact Person: {serviceCenter.contact_person}
        </Typography>
        <Typography variant="body1">
          Location: {serviceCenter.latitude}, {serviceCenter.longitude}
        </Typography>
        {serviceCenter.latitude && serviceCenter.longitude && (
          <MapContainer
            center={[serviceCenter.latitude, serviceCenter.longitude]}
            zoom={13}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker
              position={[serviceCenter.latitude, serviceCenter.longitude]}
            >
              <Popup>{serviceCenter.title}</Popup>
            </Marker>
          </MapContainer>
        )}
        {/* Tabs for Key Features and Reviews */}
        <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mt: 2 }}>
          <Tab label="Key Services" />
          <Tab label="Reviews" />
        </Tabs>

        {/* Tab Panels */}
        <TabPanel value={tabIndex} index={0}>
          {serviceCenter.key_features &&
          serviceCenter.key_features.length > 0 ? (
            <List>
              {serviceCenter.key_features.map((feature, index) => (
                <ListItem
                  key={index}
                  sx={{ borderBottom: "1px solid #ccc", mb: 1 }}
                >
                  <ListItemText
                    primary={
                      feature.icon_path && (
                        <Avatar
                          src={`${process.env.REACT_APP_STORAGE_URL}/${feature.icon_path}`}
                          alt="Feature Icon"
                        />
                      )
                    }
                    secondary={feature.description}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1" sx={{ mt: 2 }}>
              No key services available.
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          {serviceCenter.reviews.length > 0 ? (
            <List>
              {serviceCenter.reviews.map((review, index) => (
                <ListItem
                  key={index}
                  sx={{ borderBottom: "1px solid #ccc", mb: 1 }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1">
                        {review.user.full_name} - {review.rating} ⭐
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2">
                        {review.review} <br />
                        <span style={{ fontStyle: "italic" }}>
                          {formatCustomDate(review.created_at)}
                        </span>
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1" sx={{ mt: 2 }}>
              No reviews available.
            </Typography>
          )}
        </TabPanel>

        <Button variant="contained" onClick={() => onClose()}>
          Close
        </Button>
      </Box>
    </Modal>
  );
};

export default ServiceCenterDetailsModal;
